"use client";

import React from "react";
import { Plane, MapPin, Wifi, QrCode, Smile, BadgeCheck, Globe2 } from "lucide-react";
import { useTranslations } from "@/lib/i18n-client";

/**
 * eSIMCobra hero illustration — a living, brutalist travel scene.
 * Pure CSS/SVG animations (static-export safe, no canvas/JS loop).
 * All motion respects prefers-reduced-motion via the .hero-anim gate.
 */
export default function HeroScene({ fromPrice }: { fromPrice?: string }) {
  const { t } = useTranslations();

  return (
    <div className="relative w-full max-w-[300px] sm:max-w-[400px] lg:max-w-[460px] mx-auto">
      {/* Offset backing panel for the brutalist depth */}
      <div className="absolute inset-3 border-2 border-foreground bg-primary/15 rounded-md translate-x-3 translate-y-3" aria-hidden="true" />

      {/* Main stage */}
      <div
        className="relative aspect-square border-2 border-foreground bg-card rounded-md overflow-hidden shadow-[6px_6px_0_0_hsl(var(--foreground))]"
        role="img"
        dir="ltr"
        aria-label={t("hero.sceneAlt", "A plane flying between countries with instant eSIM connectivity")}
      >
        {/* Blueprint grid backdrop */}
        <div
          className="absolute inset-0 opacity-60"
          aria-hidden="true"
          style={{
            backgroundImage:
              "linear-gradient(hsl(var(--foreground) / 0.06) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground) / 0.06) 1px, transparent 1px)",
            backgroundSize: "32px 32px",
          }}
        />

        {/* Dashed flight arc (data flowing) */}
        <svg
          className="absolute inset-0 w-full h-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path
            d="M 14 82 Q 50 4 86 24"
            fill="none"
            stroke="hsl(var(--foreground))"
            strokeWidth="0.8"
            strokeDasharray="3 3"
            strokeLinecap="round"
            className="hero-anim hero-dash"
            vectorEffect="non-scaling-stroke"
          />
        </svg>

        {/* Origin pin (bottom-left) */}
        <div className="absolute left-[8%] top-[74%]" aria-hidden="true">
          <div className="flex items-center justify-center w-9 h-9 border-2 border-foreground bg-card shadow-[2px_2px_0_0_hsl(var(--foreground))]">
            <MapPin className="w-4 h-4 text-foreground" />
          </div>
        </div>

        {/* Destination pin + pulsing connectivity rings (top-right) */}
        <div className="absolute left-[78%] top-[16%]" aria-hidden="true">
          <span className="hero-anim hero-ping absolute -inset-2 rounded-full border-2 border-primary" />
          <span className="hero-anim hero-ping absolute -inset-2 rounded-full border-2 border-primary" style={{ animationDelay: "0.9s" }} />
          <div className="relative flex items-center justify-center w-9 h-9 border-2 border-foreground bg-primary text-primary-foreground shadow-[2px_2px_0_0_hsl(var(--foreground))]">
            <MapPin className="w-4 h-4" />
          </div>
        </div>

        {/* The plane in transit (flies along the arc) */}
        <div className="hero-anim hero-fly absolute z-20" aria-hidden="true">
          <div className="flex items-center justify-center w-11 h-11 border-2 border-foreground bg-warning text-warning-foreground rounded-full shadow-[3px_3px_0_0_hsl(var(--foreground))] -rotate-[28deg]">
            <Plane className="w-5 h-5 fill-current" />
          </div>
        </div>

        {/* Floating eSIM / QR chip (top-left) */}
        <div className="hero-anim hero-float absolute left-[6%] top-[10%] z-10 flex items-center gap-2 border-2 border-foreground bg-card px-2.5 py-1.5 shadow-[3px_3px_0_0_hsl(var(--foreground))]">
          <QrCode className="w-5 h-5 text-foreground" />
          <span className="text-[11px] font-bold uppercase tracking-tight leading-none">
            {t("hero.scene.esimReady", "eSIM ready")}
          </span>
        </div>

        {/* 5G signal bars chip (mid-right) */}
        <div className="hero-anim hero-float absolute right-[6%] top-[46%] z-10 flex items-center gap-2 border-2 border-foreground bg-card px-2.5 py-1.5 shadow-[3px_3px_0_0_hsl(var(--foreground))]" style={{ animationDelay: "0.6s" }}>
          <div className="flex items-end gap-[3px] h-5">
            {[0, 1, 2, 3].map((i) => (
              <span
                key={i}
                className="hero-anim hero-bar w-1.5 bg-primary border border-foreground"
                style={{ height: `${30 + i * 22}%`, animationDelay: `${i * 0.15}s` }}
              />
            ))}
          </div>
          <span className="text-[11px] font-black leading-none">5G</span>
        </div>

        {/* Happy traveler + Connected! status (bottom) */}
        <div className="absolute left-1/2 bottom-[7%] -translate-x-1/2 z-20 flex flex-col items-center gap-2">
          <div className="hero-anim hero-float-slow flex items-center justify-center w-14 h-14 border-2 border-foreground bg-secondary text-secondary-foreground rounded-full shadow-[3px_3px_0_0_hsl(var(--foreground))]">
            <Smile className="w-7 h-7" />
          </div>
          <div className="flex items-center gap-1.5 border-2 border-foreground bg-primary text-primary-foreground px-2.5 py-1 shadow-[2px_2px_0_0_hsl(var(--foreground))]">
            <BadgeCheck className="w-3.5 h-3.5" />
            <span className="text-[11px] font-black uppercase tracking-tight">
              {t("hero.scene.connected", "Connected!")}
            </span>
          </div>
        </div>

        {/* Globe watermark */}
        <Globe2
          className="absolute -left-6 -bottom-6 w-28 h-28 text-foreground/[0.05] z-0"
          aria-hidden="true"
        />

        {/* From price tag */}
        <div className="absolute right-[5%] bottom-[7%] z-20 rotate-3 border-2 border-foreground bg-card px-2.5 py-1 shadow-[3px_3px_0_0_hsl(var(--foreground))]">
          <div className="text-[9px] font-bold uppercase tracking-widest text-foreground/60 leading-none">
            {t("hero.scene.from", "From")}
          </div>
          <div className="display-xl text-2xl leading-none flex items-center gap-1">
            <Wifi className="w-4 h-4 text-primary" />{fromPrice || "$2"}
          </div>
        </div>
      </div>
    </div>
  );
}
